import { useEffect } from "react";

/**
 * Lightweight per-route SEO helper. Updates <title>, <meta name="description">,
 * Open Graph tags and the canonical link directly via the DOM — no external
 * dependency (react-helmet) needed. Restores the previous values on unmount so
 * SPA navigation always lands on the correct head state.
 */
export function useSEO({
    title,
    description,
    canonical,
    image,
    type = "website",
    noindex = false,
} = {}) {
    useEffect(() => {
        const prev = {
            title: document.title,
            description: getMeta("description")?.content,
            ogTitle: getMetaProp("og:title")?.content,
            ogDescription: getMetaProp("og:description")?.content,
            ogUrl: getMetaProp("og:url")?.content,
            ogType: getMetaProp("og:type")?.content,
            ogImage: getMetaProp("og:image")?.content,
            twitterTitle: getMeta("twitter:title")?.content,
            twitterDescription: getMeta("twitter:description")?.content,
            twitterImage: getMeta("twitter:image")?.content,
            canonical: document.querySelector('link[rel="canonical"]')?.href,
            robots: getMeta("robots")?.content,
        };

        if (title) document.title = title;
        if (description) setMeta("description", description);
        if (title) setMetaProp("og:title", title);
        if (description) setMetaProp("og:description", description);
        setMetaProp("og:type", type);
        if (canonical) {
            setMetaProp("og:url", canonical);
            setCanonical(canonical);
        } else if (typeof window !== "undefined") {
            setMetaProp("og:url", window.location.href);
            setCanonical(window.location.origin + window.location.pathname);
        }
        if (image) {
            setMetaProp("og:image", image);
            setMeta("twitter:image", image);
        }
        if (title) setMeta("twitter:title", title);
        if (description) setMeta("twitter:description", description);
        setMeta("robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");

        return () => {
            // Restore prior head state so the next route can update cleanly.
            document.title = prev.title || document.title;
            if (prev.description) setMeta("description", prev.description);
            if (prev.ogTitle) setMetaProp("og:title", prev.ogTitle);
            if (prev.ogDescription) setMetaProp("og:description", prev.ogDescription);
            if (prev.ogUrl) setMetaProp("og:url", prev.ogUrl);
            if (prev.ogType) setMetaProp("og:type", prev.ogType);
            if (prev.ogImage) setMetaProp("og:image", prev.ogImage);
            if (prev.twitterTitle) setMeta("twitter:title", prev.twitterTitle);
            if (prev.twitterDescription) setMeta("twitter:description", prev.twitterDescription);
            if (prev.twitterImage) setMeta("twitter:image", prev.twitterImage);
            if (prev.robots) setMeta("robots", prev.robots);
            if (prev.canonical) setCanonical(prev.canonical);
        };
    }, [title, description, canonical, image, type, noindex]);
}

/**
 * Inject a JSON-LD structured-data block. Returns a cleanup that removes it on
 * unmount.
 */
export function useStructuredData(data) {
    useEffect(() => {
        if (!data) return;
        const tag = document.createElement("script");
        tag.type = "application/ld+json";
        tag.text = JSON.stringify(data);
        tag.setAttribute("data-route-jsonld", "1");
        document.head.appendChild(tag);
        return () => { tag.remove(); };
    }, [JSON.stringify(data)]);
}

function getMeta(name) {
    return document.querySelector(`meta[name="${name}"]`);
}
function getMetaProp(prop) {
    return document.querySelector(`meta[property="${prop}"]`);
}
function setMeta(name, content) {
    let el = getMeta(name);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}
function setMetaProp(prop, content) {
    let el = getMetaProp(prop);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}
function setCanonical(href) {
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}
